// src/components/EmailVerificationBanner.tsx
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, useToast } from "@/ui";
import { Loader2, MailWarning } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { resendVerification } from "@/features/auth/api";

/**
 * Banner persistente top-of-page que aparece cuando el customer está
 * autenticado pero `emailVerifiedAt` es null. Auto-oculto si verifica.
 *
 * Sprint 5 Slice 2: invocado desde StorefrontTenantLayout (scope tenant).
 */
export function EmailVerificationBanner() {
  const user = useAuthStore((s) => s.user);
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const toast = useToast();
  const [resending, setResending] = useState(false);

  if (!user) return null;
  if (user.emailVerifiedAt) return null;
  if (!orgSlug) return null;

  async function handleResend() {
    if (!orgSlug || !user?.email) return;
    setResending(true);
    try {
      await resendVerification(orgSlug, user.email);
      toast({
        title: "Te enviamos un nuevo enlace.",
        variant: "success",
      });
    } catch {
      toast({
        title: "No pudimos reenviar el enlace.",
        variant: "error",
      });
    } finally {
      setResending(false);
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-b border-amber-300/40 bg-amber-50/95 dark:bg-amber-950/50"
    >
      <div className="container mx-auto flex flex-col gap-3 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-100">
          <MailWarning size={16} />
          <span>
            Verifica tu correo para completar pedidos: te enviamos un enlace.
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={resending}
          onClick={handleResend}
        >
          {resending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} />
              Reenviando…
            </span>
          ) : (
            "Reenviar enlace"
          )}
        </Button>
      </div>
    </div>
  );
}
